/**
 * TELA — CRM
 *
 * O funil da equipe comercial. É a primeira tela do sistema cujo usuário não
 * é quem cuida da infraestrutura: é quem vende. Isso muda o padrão de leitura
 * — aqui a pergunta não é "o parque está saudável?", e sim "com quem eu falo
 * agora, e o que ficou combinado com ele?".
 *
 * Quatro visões sobre a MESMA carga (ver `CrmVisoes.tsx`), três gavetas por
 * cliente (ver `CrmGavetas.tsx`), e duas portas de entrada: cadastro manual e
 * importação em massa.
 *
 * A REGRA QUE ATRAVESSA A TELA: nada aqui é um cadastro paralelo. O card
 * aponta para o mesmo contato que a campanha alvejou e que aparece na
 * conversa — UM CLIENTE · UM HISTÓRICO · UMA CONVERSA · UM CRM.
 */
import { useCallback, useMemo, useState } from "react";
import type { CardCrm, UsuarioResumo } from "@co/contracts";
import { api, type RespostaCrm } from "../api.js";
import {
  Aviso,
  Botao,
  Campo,
  Indicador,
  Painel,
  classeEntrada,
} from "../ui/base.js";
import { ErroTela, useCarga } from "../ui/carga.js";
import { classeFiltro } from "../ui/dados.js";
import { Modal } from "../ui/gaveta.js";
import { num, plural } from "../ui/formato.js";
import { Foco, Funil, Kanban, Lista, type AcoesDoCard } from "./CrmVisoes.js";
import {
  GavetaConversa,
  GavetaFicha,
  GavetaHistorico,
  ModalEtapas,
  ModalImportar,
  ModalNovoCliente,
} from "./CrmGavetas.js";

type Visao = "kanban" | "lista" | "funil" | "foco";

const VISOES: ReadonlyArray<{ id: Visao; nome: string; dica: string }> = [
  { id: "kanban", nome: "Kanban", dica: "O quadro inteiro. Arraste o cartão para mudar de etapa." },
  { id: "lista", nome: "Lista", dica: "Uma linha por cliente — para achar alguém e comparar." },
  { id: "funil", nome: "Funil", dica: "Onde os clientes empacam. Clique numa faixa para ver quem está nela." },
  { id: "foco", nome: "Foco", dica: "Uma etapa por vez, para trabalhar a lista sem se distrair." },
];

const CHAVE_VISAO = "autodis.crm.visao";

function lerVisao(): Visao {
  try {
    const v = localStorage.getItem(CHAVE_VISAO);
    return VISOES.some((x) => x.id === v) ? (v as Visao) : "kanban";
  } catch {
    // Navegador com armazenamento bloqueado não pode derrubar a tela.
    return "kanban";
  }
}

const MOEDA = new Intl.NumberFormat("pt-BR", {
  style: "currency",
  currency: "BRL",
  maximumFractionDigits: 0,
});

type Aberta =
  | { tipo: "conversa" | "historico" | "ficha"; card: CardCrm }
  | { tipo: "novo" | "importar" | "etapas" }
  | null;

export function Crm(): JSX.Element {
  const [visao, setVisao] = useState<Visao>(lerVisao);
  const [busca, setBusca] = useState("");
  const [buscaAplicada, setBuscaAplicada] = useState("");
  const [responsavel, setResponsavel] = useState("");
  const [etapaAberta, setEtapaAberta] = useState<string | null>(null);
  const [aberta, setAberta] = useState<Aberta>(null);
  const [erroAcao, setErroAcao] = useState<string | null>(null);
  // Mover para uma etapa que FECHA como perdido exige motivo — é a única
  // informação que o funil produz de graça: por que a gente perde.
  const [pedirMotivo, setPedirMotivo] = useState<{ card: CardCrm; etapaId: string } | null>(null);
  const [motivo, setMotivo] = useState("");

  const chave = `${buscaAplicada}|${responsavel}`;
  const { carga, recarregar } = useCarga<RespostaCrm>(
    () => api.crm({ busca: buscaAplicada || undefined, responsavel: responsavel || undefined }),
    chave,
  );
  const { carga: cargaUsuarios } = useCarga<{ itens: UsuarioResumo[] }>(
    () => api.usuarios(),
    "usuarios",
  );
  const usuarios = cargaUsuarios.fase === "pronto" ? cargaUsuarios.dados.itens : [];

  const trocarVisao = useCallback((v: Visao) => {
    setVisao(v);
    try {
      localStorage.setItem(CHAVE_VISAO, v);
    } catch {
      /* preferência é conveniência, não requisito */
    }
  }, []);

  const mover = useCallback(
    async (card: CardCrm, etapaId: string, motivoTexto?: string) => {
      setErroAcao(null);
      try {
        await api.crmMover(card.id, {
          etapaId,
          ...(motivoTexto ? { motivo: motivoTexto } : {}),
        });
        recarregar();
      } catch (e) {
        setErroAcao(e instanceof Error ? e.message : "Não consegui mover o cliente.");
      }
    },
    [recarregar],
  );

  const acoes: AcoesDoCard = useMemo(
    () => ({
      aoAbrirConversa: (card) => setAberta({ tipo: "conversa", card }),
      aoAbrirHistorico: (card) => setAberta({ tipo: "historico", card }),
      aoAbrirFicha: (card) => setAberta({ tipo: "ficha", card }),
      aoMover: (card, etapaId) => {
        if (carga.fase !== "pronto") return;
        const destino = carga.dados.etapas.find((e) => e.id === etapaId);
        if (destino?.tipo === "perdido") {
          setMotivo("");
          setPedirMotivo({ card, etapaId });
          return;
        }
        void mover(card, etapaId);
      },
    }),
    [carga, mover],
  );

  if (carga.fase === "erro") {
    return <ErroTela mensagem={carga.mensagem} aoTentar={recarregar} />;
  }

  const dados = carga.fase === "pronto" ? carga.dados : null;
  const etapas = dados?.etapas ?? [];
  const cards = dados?.cards ?? [];

  return (
    <div className="flex flex-col gap-4">
      {/* ─── Barra de trabalho ──────────────────────────────────── */}
      <div className="flex items-center gap-2 flex-wrap">
        <div
          role="group"
          aria-label="Como ver o funil"
          className="flex gap-1 bg-superficie border border-linha rounded-lg p-1"
        >
          {VISOES.map((v) => (
            <button
              key={v.id}
              type="button"
              onClick={() => trocarVisao(v.id)}
              aria-pressed={visao === v.id}
              title={v.dica}
              className={`rounded-md px-3 py-1.5 text-[12.5px] transition-colors ${
                visao === v.id
                  ? "bg-marca-suave text-marca-tinta font-medium"
                  : "text-tinta-2 hover:bg-superficie-3"
              }`}
            >
              {v.nome}
            </button>
          ))}
        </div>

        <form
          className="flex gap-2 items-center"
          onSubmit={(e) => {
            e.preventDefault();
            setBuscaAplicada(busca.trim());
          }}
        >
          <input
            aria-label="Buscar cliente"
            placeholder="Buscar por empresa, pessoa, e-mail ou telefone"
            value={busca}
            onChange={(e) => setBusca(e.target.value)}
            className={`${classeFiltro} min-w-[260px]`}
          />
          <Botao pequeno submit>
            Buscar
          </Botao>
          {buscaAplicada !== "" && (
            <Botao
              pequeno
              tipo="fantasma"
              onClick={() => {
                setBusca("");
                setBuscaAplicada("");
              }}
            >
              Limpar
            </Botao>
          )}
        </form>

        <div className="ml-auto flex gap-2 items-center flex-wrap">
          <select
            aria-label="Filtrar por responsável"
            value={responsavel}
            onChange={(e) => setResponsavel(e.target.value)}
            className={classeFiltro}
          >
            <option value="">Todos os responsáveis</option>
            {(dados?.responsaveis ?? []).map((r) => (
              <option key={r.id} value={r.id}>
                {r.nome} ({r.cards})
              </option>
            ))}
          </select>
          <Botao pequeno onClick={() => setAberta({ tipo: "etapas" })}>
            Etapas
          </Botao>
          <Botao pequeno onClick={() => setAberta({ tipo: "importar" })}>
            Importar
          </Botao>
          <Botao pequeno tipo="principal" onClick={() => setAberta({ tipo: "novo" })}>
            + Cliente
          </Botao>
        </div>
      </div>

      {/*
        A dica da visão e a legenda das bolinhas ficam LOGO ABAIXO da barra,
        não no rodapé. No rodapé elas estavam depois do conteúdo — ou seja,
        quem abriu a tela pela primeira vez só encontrava a explicação depois
        de já ter rolado o quadro inteiro sem entender.
      */}
      <div className="flex items-center gap-x-4 gap-y-1.5 flex-wrap text-[11.5px] text-tinta-3">
        <span>{VISOES.find((v) => v.id === visao)?.dica}</span>
        <span className="flex items-center gap-3 ml-auto">
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="size-1.5 rounded-full bg-ok" /> em dia
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="size-1.5 rounded-full bg-warn" /> esfriando
          </span>
          <span className="inline-flex items-center gap-1.5">
            <span aria-hidden="true" className="size-1.5 rounded-full bg-crit" /> parado há 7 d+
          </span>
        </span>
      </div>

      {erroAcao && <Aviso tom="crit">{erroAcao}</Aviso>}

      {/* ─── O que o quadro está dizendo ────────────────────────── */}
      {dados && (
        <>
          <div className="grid gap-3 grid-cols-2 lg:grid-cols-4">
            <Indicador
              rotulo="Em andamento"
              valor={num(dados.resumo.abertos)}
              nota={`de ${plural(dados.resumo.total, "cliente no funil", "clientes no funil")}`}
            />
            <Indicador
              rotulo="Valor em aberto"
              valor={MOEDA.format(dados.resumo.valorAberto)}
              nota="soma só o que ainda está em jogo"
              tom="marca"
            />
            <Indicador
              rotulo="Parados há 7 d+"
              valor={num(dados.resumo.semContatoHa7Dias)}
              nota="na mesma etapa, sem andar"
              {...(dados.resumo.semContatoHa7Dias > 0 ? { tom: "warn" as const } : {})}
            />
            <Indicador
              rotulo="Ganhos"
              valor={num(dados.resumo.ganhos)}
              nota={`${num(dados.resumo.perdidos)} perdidos`}
              tom="ok"
            />
          </div>

          {dados.avisos.length > 0 && (
            <div className="flex flex-col gap-2">
              {dados.avisos.map((a) => (
                <Aviso key={a} tom="warn">
                  {a}
                </Aviso>
              ))}
            </div>
          )}
        </>
      )}

      {/* ─── A visão escolhida ──────────────────────────────────── */}
      {carga.fase === "carregando" && (
        <Painel>
          <p className="text-[13px] text-tinta-3 py-8 text-center">Carregando o funil…</p>
        </Painel>
      )}

      {dados && cards.length === 0 && buscaAplicada === "" && (
        <Painel titulo="O funil está vazio">
          <p className="text-[13px] text-tinta-2 leading-relaxed">
            Duas formas de começar: cadastrar um cliente à mão, ou trazer em massa —
            de uma planilha já importada, ou de quem já respondeu a uma campanha.
          </p>
          <div className="flex gap-2 mt-3 flex-wrap">
            <Botao tipo="principal" onClick={() => setAberta({ tipo: "novo" })}>
              Cadastrar cliente
            </Botao>
            <Botao onClick={() => setAberta({ tipo: "importar" })}>Trazer em massa</Botao>
          </div>
        </Painel>
      )}

      {dados && (cards.length > 0 || buscaAplicada !== "") && (
        <>
          {visao === "kanban" && <Kanban etapas={etapas} cards={cards} acoes={acoes} />}
          {visao === "lista" && (
            <Painel semCorpo>
              <Lista etapas={etapas} cards={cards} acoes={acoes} />
            </Painel>
          )}
          {visao === "funil" && (
            <Funil
              etapas={etapas}
              cards={cards}
              acoes={acoes}
              etapaAberta={etapaAberta}
              aoAbrirEtapa={setEtapaAberta}
            />
          )}
          {visao === "foco" && (
            <Foco
              etapas={etapas}
              cards={cards}
              acoes={acoes}
              etapaAberta={etapaAberta}
              aoAbrirEtapa={setEtapaAberta}
            />
          )}
        </>
      )}

      {/* ─── Gavetas e formulários ──────────────────────────────── */}
      {aberta?.tipo === "conversa" && (
        <GavetaConversa card={aberta.card} aoFechar={() => setAberta(null)} aoMudar={recarregar} />
      )}
      {aberta?.tipo === "historico" && (
        <GavetaHistorico card={aberta.card} aoFechar={() => setAberta(null)} aoMudar={recarregar} />
      )}
      {aberta?.tipo === "ficha" && (
        <GavetaFicha
          card={aberta.card}
          etapas={etapas}
          usuarios={usuarios}
          aoFechar={() => setAberta(null)}
          aoMudar={recarregar}
        />
      )}
      {aberta?.tipo === "novo" && (
        <ModalNovoCliente
          etapas={etapas}
          usuarios={usuarios}
          aoFechar={() => setAberta(null)}
          aoCriar={recarregar}
        />
      )}
      {aberta?.tipo === "importar" && (
        <ModalImportar
          etapas={etapas}
          usuarios={usuarios}
          aoFechar={() => setAberta(null)}
          aoImportar={recarregar}
        />
      )}
      {aberta?.tipo === "etapas" && (
        <ModalEtapas aoFechar={() => setAberta(null)} aoMudar={recarregar} />
      )}

      {pedirMotivo && (
        <Modal
          titulo="Por que perdemos este cliente?"
          dica="É a única informação que o funil produz de graça — e a que ensina a próxima abordagem. Sem ela o card não se move."
          largura={520}
          aoFechar={() => setPedirMotivo(null)}
          rodape={
            <>
              <Botao onClick={() => setPedirMotivo(null)}>Cancelar</Botao>
              <Botao
                tipo="principal"
                desabilitado={motivo.trim().length < 3}
                onClick={() => {
                  const alvo = pedirMotivo;
                  setPedirMotivo(null);
                  void mover(alvo.card, alvo.etapaId, motivo.trim());
                }}
              >
                Marcar como perdido
              </Botao>
            </>
          }
        >
          <Campo rotulo={`Motivo — ${pedirMotivo.card.razaoSocial}`}>
            <textarea
              autoFocus
              rows={3}
              className={`${classeEntrada} resize-none`}
              placeholder="Ex.: fechou com a concorrente por preço; ficou de rever em janeiro."
              value={motivo}
              onChange={(e) => setMotivo(e.target.value)}
            />
          </Campo>
        </Modal>
      )}


    </div>
  );
}
